<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import bienIciMap from '@utils/bien-ici-map'
import { findBounds } from '@utils/map-helpers'

export default {
  extends: bienIciMap,

  props: {
    searchParams: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      markers: [],
      map: null
    }
  },

  computed: {
    ...mapGetters({
      accommodations: 'getAccommodations'
    }),

    mutableProperties () {
      if (this.map && this.accommodations.length) {
        return this.map, this.accommodations, Date.now() // eslint-disable-line
      }

      return false
    }
  },

  watch: {
    mutableProperties () {
      this.onLoaded()
    }
  },

  mounted () {
    axios.get('/api/v1/accommodations', { params: this.searchParams })
      .then(({ data }) => {
        this.$store.commit('setAccommodations', data.data)
      })
  },

  methods: {
    onLoad (_realEstateAd, map) {
      this.map = map
    },

    onLoaded () {
      this.markers.forEach(marker => this.map.removeLayer(marker))
      this.markers = []

      this.map.fitBounds(findBounds(this.accommodations))

      this.accommodations.forEach(({ id, attributes }) => {
        let virtualVisitUrlPicto = ""
        if (attributes.virtualVisitUrl) {
          virtualVisitUrlPicto =
            `
              <a class="thumb-pop-in__icon inline-block w-10" href="${attributes.virtualVisitUrl}" target="_blank" rel="noopener">
                <div class="thumb-pop-in__icon-bg relative w-10 h-10 flex justify-center items-center rounded-full font-bold mx-auto">
                  <div class="text-white absolute text-xl font-sans top-1/2 left-0 right-0 mx-auto text-center transform -translate-y-1/2">
                    <svg aria-hidden="true" focusable="false" data-prefix="fal" data-icon="street-view" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" class="text-xs svg-inline--fa fa-street-view fa-w-16 ">
                      <path fill="currentColor" d="M368 360.15v30.87c62.27 8.42 104 23.6 104 40.99 0 26.51-96.71 48-216 48S40 458.51 40 432c0-17.38 41.73-32.56 104-40.99v-30.87C58.82 373.14 0 400.39 0 432c0 44.18 114.62 80 256 80s256-35.82 256-80c0-31.61-58.82-58.86-144-71.85zM176 320v80c0 26.47 21.53 48 48 48h64c26.47 0 48-21.53 48-48v-80c26.47 0 48-21.53 48-48v-64c0-34.53-22.11-63.75-52.82-74.93C339.14 119.84 344 104.54 344 88c0-48.52-39.48-88-88-88s-88 39.48-88 88c0 16.54 4.86 31.84 12.82 45.07C150.11 144.25 128 173.47 128 208v64c0 26.47 21.53 48 48 48zm80-288c30.93 0 56 25.07 56 56s-25.07 56-56 56-56-25.07-56-56 25.07-56 56-56zm-96 176c0-25.76 20.44-46.68 45.93-47.79C220.17 170.11 237.38 176 256 176s35.83-5.89 50.07-15.79C331.56 161.32 352 182.24 352 208v64c0 8.83-7.19 16-16 16h-32v112c0 8.83-7.19 16-16 16h-64c-8.81 0-16-7.17-16-16V288h-32c-8.81 0-16-7.17-16-16v-64z" class=""></path>
                    </svg>
                  </div>
                </div>
              </a>
            `
        }

        let videoUrlPicto = ""
        if (attributes.videoUrl) {
          videoUrlPicto =
            `
              <a class="thumb-pop-in__icon inline-block w-10" href="${attributes.videoUrl}" target="_blank" rel="noopener">
                <div class="thumb-pop-in__icon-bg relative w-10 h-10 flex justify-center items-center rounded-full font-bold mx-auto">
                  <div class="text-white absolute text-xl font-sans top-1/2 left-0 right-0 mx-auto text-center transform -translate-y-1/2">
                    <svg aria-hidden="true" focusable="false" data-prefix="fal" data-icon="play" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" class="text-xs svg-inline--fa fa-play fa-w-14">
                      <path fill="currentColor" d="M424.4 214.7L72.4 6.6C43.8-10.3 0 6.1 0 47.9V464c0 37.5 40.7 60.1 72.4 41.3l352-208c31.4-18.5 31.5-64.1 0-82.6zm-16.2 55.1l-352 208C45.6 483.9 32 476.6 32 464V47.9c0-16.3 16.4-18.4 24.1-13.8l352 208.1c10.5 6.2 10.5 21.4.1 27.6z" class=""></path>
                    </svg>
                  </div>
                </div>
              </a>
            `
        }

        const popup = L.popup({
          minWidth: 220,
          maxWidth: 220
        }).setContent(`
           <a href="${attributes.seoAccommodationPath}" onclick="void(0)" target="_blank" rel="noopener">
            <div class="thumb thumb-pop-in relative shadow bg-white w-thumb-pop-in mx-auto">
              <div class="img-ratio">
                <img class="img-cover lazy-load loaded" src="${attributes.pictureUrl}" loading="lazy" />
              </div>

              <div class="py-6 px-8">
                <span class="thumb-pop-in__title font-sans text-xl text-gray leading-tight pb-3">
                  ${attributes.title}
                </span>
                <p class="font-sans text-primary text-xl">
                  ${attributes.price}
                </p>
                <div class="absolute bottom-4 right-16 mt-3">
                  ${virtualVisitUrlPicto}
                  ${videoUrlPicto}
                </div>
              </div>
            </div>
          </a>
        `)

        const marker = new L.Marker(
          {
            lat: attributes.latitude,
            lng: attributes.longitude
          },
          {
            icon: L.divIcon({
              className: `bg-map-icon transition transition-colors duration-300 flex justify-center font-sans text-xs font-medium text-white rounded icon-marker-${id}`,
              html: `<p class="px-2">${attributes.formattedPrice}</p>`,
              iconSize: ["auto"],
              iconAnchor: [0, 0]
            })
          }
        )
        .addTo(this.map)
        .bindPopup(popup)

        marker.addEventListener('mouseover', function () {
          this.openPopup()
        })

        marker.addEventListener('mouseout', function () {
          // Only if the devise has a mouse.
          if (matchMedia('(pointer:fine)').matches) {
            this.closePopup()
          }
        })

        marker.addEventListener('click', function () {
          window.open(attributes.seoAccommodationPath, '_blank')
        })

        this.markers.push(marker)
      })

      document.querySelectorAll('[data-card-maker]').forEach(el => {
        const id = el.dataset.cardMaker
        const icon = document.querySelector(`.icon-marker-${id}`)

        el.addEventListener('mouseover', () => icon.classList.add('hover'))
        el.addEventListener('mouseleave', () => icon.classList.remove('hover'))
      })
    }
  }
}
</script>
